import type { PropertyType, UserRole } from '@/types';

/**
 * Property-type helpers used across the PM / Building Manager surface.
 *
 * The UI groups the schema's fine-grained `property_type` enum into four
 * shapes that drive layout, navigation, and module behaviour:
 *
 *   residential        — gated community, residential apartment, master-planned city
 *   commercial_single  — one company occupies the entire building
 *   commercial_multi   — office tower or business park with multiple companies
 *   mixed              — both residential and commercial flows coexist
 *
 * The mapping from schema enum to this UI vocabulary lives server-side in
 * `toPmPropertyType`. The client only sees and branches on this four-way.
 */

export function isCommercial(t: PropertyType | undefined): boolean {
  return t === 'commercial_single' || t === 'commercial_multi';
}

export function isSingleTenant(t: PropertyType | undefined): boolean {
  return t === 'commercial_single';
}

export function isMultiTenant(t: PropertyType | undefined): boolean {
  return t === 'commercial_multi';
}

export function isResidential(t: PropertyType | undefined): boolean {
  return t === 'residential';
}

export function isMixedUse(t: PropertyType | undefined): boolean {
  return t === 'mixed';
}

/**
 * Show resident-facing screens (Recurring, Domestic, Resident Favourites,
 * Residents tab) only on properties that actually host residents. A pure
 * commercial building has no residents — surfacing those screens there is
 * confusing dead UI.
 */
export function showResidentSurfaces(t: PropertyType | undefined): boolean {
  return t === 'residential' || t === 'mixed' || t === undefined;
}

/**
 * Show enterprise-tenant screens (Companies tab, walk-in routing to companies,
 * worker passes scoped per company) on commercial and mixed-use properties.
 */
export function showCommercialSurfaces(t: PropertyType | undefined): boolean {
  return isCommercial(t) || t === 'mixed';
}

const PM_ROLE_LABELS: Record<'commercial' | 'other', string> = {
  commercial: 'Building Manager',
  other: 'Property Manager',
};

/**
 * Human-readable label for the property's deployment shape — used wherever the
 * UI needs to *display* the type (PropertySetupPage read-only, summary cards,
 * audit logs). Distinct from the Org-Admin dropdown which speaks the granular
 * schema enum.
 */
export function getPropertyTypeLabel(t: PropertyType | undefined): string {
  switch (t) {
    case 'residential':
      return 'Residential';
    case 'commercial_single':
      return 'Enterprise — Single Company';
    case 'commercial_multi':
      return 'Enterprise — Multiple Companies';
    case 'mixed':
      return 'Mixed-Use';
    default:
      return 'Unknown';
  }
}

/**
 * The underlying server role (`property_manager`) is stable across property
 * shapes. Only the UI label varies — "Property Manager" on residential and
 * mixed, "Building Manager" on commercial. ADR 008 §Decision row.
 */
export function getRoleLabel(role: UserRole, propertyType: PropertyType | undefined): string {
  if (role === 'property_manager') {
    return isCommercial(propertyType) ? PM_ROLE_LABELS.commercial : PM_ROLE_LABELS.other;
  }
  return defaultRoleLabel(role);
}

function defaultRoleLabel(role: UserRole): string {
  switch (role) {
    case 'property_manager':
      return 'Property Manager';
    case 'security_supervisor':
      return 'Security Supervisor';
    case 'guard':
      return 'Guard';
    case 'resident':
      return 'Resident';
    case 'enterprise_tenant_admin':
      return 'Tenant Admin';
    case 'employee':
      return 'Employee';
    case 'org_admin':
      return 'Org Admin';
    default:
      return role;
  }
}
